"use client";

import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import {
  clearCredentials,
  setCredentials,
} from "@/src/store/slices/authSlice";

type RefreshSessionResponse = {
  access: string;
};

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  useEffect(() => {
    if (accessToken) return;

    let isMounted = true;

    const restoreSession = async () => {
      try {
        const response = await fetch("/api/auth?optional=1", {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin",
        });

        if (response.status === 204) {
          if (isMounted) {
            dispatch(clearCredentials());
          }
          return;
        }

        if (!response.ok) {
          if (response.status === 401 && isMounted) {
            dispatch(clearCredentials());
          }
          return;
        }

        const data = (await response.json()) as RefreshSessionResponse;

        if (!isMounted) return;

        dispatch(
          setCredentials({
            accessToken: data.access,
            userId: null,
          })
        );
      } catch {
        if (isMounted) {
          dispatch(clearCredentials());
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, [accessToken, dispatch]);

  return children;
}
